import { useEffect, useState, type ReactElement } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ToastContainer } from "@/components/ui/Toast";
import { useToast, type UseToastResult } from "@/hooks/useToast";
import { strategiesApi } from "@/lib/api";
import { describeError } from "@/lib/http";
import { type Strategy } from "@/types/strategy";
import { StrategyPositionSection } from "@/components/Positional/StrategyBasketSection";
import { ExecutionModeSwitch } from "./ExecutionModeSwitch";
import { HorizonSwitch } from "./HorizonSwitch";
import { EmaTrendPanels } from "./EmaTrendPanels";
import { Poi1Panels } from "./Poi1Panels";
import { ManualExitRules, ManualTradingPanels } from "./ManualTradingPanels";
import { EmaTrendStructurePill } from "./EmaTrendStructurePill";
import { MarketStatusRow } from "./MarketStatusRow";
import { PayoffCard } from "./PayoffCard";
import { StrategyControlsCard } from "./StrategyControlsCard";
import { ThetaControlsCard } from "./ThetaControlsCard";
import { StrategyAlertSoundCard } from "./StrategyAlertSoundCard";
import {
  DetailDialogs,
  type DialogFlags,
  type MutHooks,
  type RevealState,
} from "./StrategyDetailDialogs";
import { STRATEGIES_KEY } from "./useStrategyActions";

function useDocumentTitle(title: string): void {
  useEffect(() => {
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

function useDialogFlags(): [DialogFlags, (k: keyof DialogFlags, v: boolean) => void] {
  const [flags, setFlags] = useState<DialogFlags>({ kill: false, del: false });
  return [
    flags,
    (k, v) => {
      setFlags((s) => ({ ...s, [k]: v }));
    },
  ];
}

interface MutCtx {
  id: string;
  toast: UseToastResult;
  setReveal: (r: RevealState) => void;
  setFlag: (k: keyof DialogFlags, v: boolean) => void;
  invalidate: () => void;
}

function useDetailMutations(
  id: string,
  toast: UseToastResult,
  setReveal: (r: RevealState) => void,
  setFlag: (k: keyof DialogFlags, v: boolean) => void,
): MutHooks {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const ctx: MutCtx = {
    id,
    toast,
    setReveal,
    setFlag,
    invalidate: () => {
      void qc.invalidateQueries({ queryKey: STRATEGIES_KEY });
    },
  };
  return {
    killMut: useKillMutation(ctx),
    deleteMut: useDeleteMutation(ctx, navigate),
  };
}

function useKillMutation(ctx: MutCtx): MutHooks["killMut"] {
  return useMutation({
    mutationFn: (kill: boolean) => strategiesApi.update(ctx.id, { kill_switch: kill }),
    onSuccess: () => {
      ctx.invalidate();
      ctx.setFlag("kill", false);
      ctx.toast.push("success", "Updated");
    },
    onError: (err) => {
      ctx.toast.push("error", `Update failed: ${describeError(err)}`);
    },
  });
}

function useDeleteMutation(
  ctx: MutCtx,
  navigate: ReturnType<typeof useNavigate>,
): MutHooks["deleteMut"] {
  return useMutation({
    mutationFn: () => strategiesApi.remove(ctx.id),
    onSuccess: () => {
      ctx.invalidate();
      ctx.toast.push("success", "Strategy deleted");
      void navigate("/strategies", { replace: true });
    },
    onError: (err) => {
      ctx.toast.push("error", `Delete failed: ${describeError(err)}`);
    },
  });
}

export function StrategyDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [flags, setFlag] = useDialogFlags();
  const [reveal, setReveal] = useState<RevealState>(null);
  const list = useQuery({ queryKey: STRATEGIES_KEY, queryFn: () => strategiesApi.list() });
  const strategy = list.data?.find((s) => s.id === id) ?? null;
  const muts = useDetailMutations(id ?? "", toast, setReveal, setFlag);

  if (list.isPending) {
    return (
      <main className="mx-auto max-w-6xl px-6 py-6 text-sm text-ink-secondary">Loading...</main>
    );
  }
  if (strategy === null) return <NotFound />;
  return (
    <DetailView
      strategy={strategy}
      flags={flags}
      setFlag={setFlag}
      reveal={reveal}
      setReveal={setReveal}
      muts={muts}
      toast={toast}
    />
  );
}

interface DetailViewProps {
  strategy: Strategy;
  flags: DialogFlags;
  setFlag: (k: keyof DialogFlags, v: boolean) => void;
  reveal: RevealState;
  setReveal: (r: RevealState) => void;
  muts: MutHooks;
  toast: UseToastResult;
}

function DetailView(props: Readonly<DetailViewProps>): ReactElement {
  const { strategy, flags, setFlag, reveal, setReveal, muts, toast } = props;
  useDocumentTitle(`${strategy.name} — NSS`);
  return (
    <main className="mx-auto max-w-6xl px-6 py-6">
      <BackLink />
      <DetailBody strategy={strategy} setFlag={setFlag} toast={toast} />
      <DetailDialogs
        strategy={strategy}
        flags={flags}
        setFlag={setFlag}
        muts={muts}
        reveal={reveal}
        setReveal={setReveal}
      />
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </main>
  );
}

interface DetailBodyProps {
  strategy: Strategy;
  setFlag: (k: keyof DialogFlags, v: boolean) => void;
  toast: UseToastResult;
}

function DetailBody({ strategy, setFlag, toast }: Readonly<DetailBodyProps>): ReactElement {
  return (
    <>
      <DetailHeader
        strategy={strategy}
        onKill={() => {
          setFlag("kill", true);
        }}
        onDelete={() => {
          setFlag("del", true);
        }}
      />
      {/* POI-1 picks its own index and strike (docs/poi1.md 2) — the shared
          day-rule instrument row would name SENSEX on a Wednesday it trades
          NIFTY. Its status card carries the spot instead. */}
      {strategy.strategy_type !== "poi1" && strategy.strategy_type !== "manual" && (
        <MarketStatusRow />
      )}
      {/* Open position FIRST (operator, 2026-08-07): when a trade is live
          the money at risk is the thing to see, not the zone hunt — an
          open PUT sat unnoticed for an hour under the status cards. While
          flat it collapses to a one-line empty strip, so the zone editor
          stays within reach for morning marking. */}
      <div className="mb-3">
        <StrategyPositionSection strategy={strategy} />
      </div>
      <ManualExitRules strategy={strategy} />
      <StatGrid />
      <ControlsCardForType strategy={strategy} toast={toast} />
      <PayoffCard strategy={strategy} />
      <StrategyAlertSoundCard strategyId={strategy.id} mode={strategy.execution_mode} />
      <ActivityCard />
      <EmaTrendPanels strategy={strategy} />
      <Poi1Panels strategy={strategy} />
      <ManualTradingPanels strategy={strategy} />
    </>
  );
}

/** Daily-config controls for intraday strategies; the theta card —
 * sizing + manual entry, no daily config — for positional_theta
 * (docs/positional_theta.md). None for POI-1: its index, strike and lots
 * come from the rule (docs/poi1.md 2), so a lots or ITM field would be a
 * control that does nothing. */
function ControlsCardForType({
  strategy,
  toast,
}: Readonly<{ strategy: Strategy; toast: UseToastResult }>): ReactElement | null {
  // The rule fixes the index, the strike and the lots, so a lots or ITM field would do nothing.
  if (
    strategy.strategy_type === "poi1" ||
    strategy.strategy_type === "failed_breakout" ||
    strategy.strategy_type === "stpivot_nifty" ||
    strategy.strategy_type === "stpivot_sensex" ||
    strategy.strategy_type === "manual" // the chart sends the lots
  )
    return null;
  if (strategy.strategy_type === "positional_theta") {
    return (
      <ThetaControlsCard
        strategy={strategy}
        onSaved={(msg) => {
          toast.push("success", msg);
        }}
        onError={(msg) => {
          toast.push("error", msg);
        }}
      />
    );
  }
  return (
    <StrategyControlsCard
      strategy={strategy}
      onSaved={() => {
        toast.push("success", "Trading controls saved for today");
      }}
      onError={(msg) => {
        toast.push("error", msg);
      }}
    />
  );
}

function NotFound(): ReactElement {
  return (
    <main className="mx-auto max-w-6xl px-6 py-6">
      <p className="text-sm text-danger">Strategy not found.</p>
      <Link to="/strategies" className="mt-3 inline-block text-sm text-accent">
        <i className="ti ti-arrow-left text-sm" /> Back to strategies
      </Link>
    </main>
  );
}

function BackLink(): ReactElement {
  return (
    <Link
      to="/strategies"
      className="mb-3 inline-flex items-center gap-1 text-sm text-ink-secondary hover:text-ink"
    >
      <i className="ti ti-arrow-left text-sm" /> Back to strategies
    </Link>
  );
}

interface DetailHeaderProps {
  strategy: Strategy;
  onKill: () => void;
  onDelete: () => void;
}

function DetailHeader({ strategy, onKill, onDelete }: Readonly<DetailHeaderProps>): ReactElement {
  return (
    <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <h1>{strategy.name}</h1>
          <StatePill killOn={strategy.kill_switch} />
          {/* Live vs Paper applies to every strategy type. */}
          <ExecutionModeSwitch strategy={strategy} />
          {/* Intraday vs Positional (docs/positional.md phase E). */}
          <HorizonSwitch strategy={strategy} />
          {/* 5/50 EMA only: market-structure direction (Case 1's side). */}
          <EmaTrendStructurePill strategy={strategy} />
        </div>
        <div className="font-mono text-xs text-ink-muted">{strategy.id}</div>
      </div>
      <div className="flex flex-wrap gap-2">
        {/* Cockpit is a standalone tab — Reports opens in its own tab so
            the live-trade view is never navigated away mid-session. No
            `rel`: same-origin, and `noreferrer` implies `noopener`, which
            would isolate it from the shared TradingView window. */}
        {/* eslint-disable-next-line react/jsx-no-target-blank -- the href is
            a same-origin app route built from our own uuid; the rule only
            fires because it is a template literal it cannot prove relative. */}
        <a
          href={`/reports/${strategy.id}`}
          target="_blank"
          className="btn btn-secondary"
          title="Open this strategy's monthly exit-style report"
        >
          <i className="ti ti-report-analytics text-base" />
          Reports
        </a>
        <button type="button" className="btn btn-warning" onClick={onKill}>
          <i className="ti ti-player-pause text-base" />
          {strategy.kill_switch ? "Re-enable" : "Kill switch"}
        </button>
        <button type="button" className="btn btn-secondary text-danger" onClick={onDelete}>
          <i className="ti ti-trash text-base" />
          Delete
        </button>
      </div>
    </header>
  );
}

function StatePill({ killOn }: Readonly<{ killOn: boolean }>): ReactElement {
  if (killOn) {
    return (
      <span className="pill bg-warning-bg text-warning">
        <i className="ti ti-player-pause text-sm" /> Kill switch ON
      </span>
    );
  }
  return (
    <span className="pill bg-success-bg text-success">
      <i className="ti ti-circle-check text-sm" /> Armed
    </span>
  );
}

function StatGrid(): ReactElement {
  return (
    <div className="mb-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Stat label="Today's P&L" value="-" hint="Coming soon" />
      <Stat label="Trades today" value="-" hint="Coming soon" />
      <Stat label="7-day P&L" value="-" hint="Coming soon" />
      <Stat label="Last alert" value="-" hint="Coming soon" />
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
}: Readonly<{ label: string; value: string; hint?: string }>): ReactElement {
  return (
    <div className="stat-card">
      <div className="text-xs text-ink-secondary">{label}</div>
      <div className="mt-1 text-2xl font-medium">{value}</div>
      {hint !== undefined && <div className="mt-1 text-xs text-ink-muted">{hint}</div>}
    </div>
  );
}

function ActivityCard(): ReactElement {
  return (
    <section className="card">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <i className="ti ti-history text-lg text-accent" />
          <h3>Recent activity</h3>
        </div>
        <span className="text-xs text-ink-muted">Coming soon</span>
      </div>
      <p className="text-sm text-ink-secondary">
        Per-strategy alerts and trail steps will appear here once the activity-feed endpoint ships.
      </p>
    </section>
  );
}
